import { useState, useCallback, useEffect } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent } from "./ui/dialog";
import { Slider } from "./ui/slider";
import { Button } from "./ui/button";
import { ZoomIn, Check, X } from "lucide-react";

// Produces a cropped JPEG File from the chosen region.
async function getCroppedFile(imageSrc, pixels, fileName) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", reject);
    img.src = imageSrc;
  });
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(pixels.width);
  canvas.height = Math.round(pixels.height);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    pixels.x, pixels.y, pixels.width, pixels.height,
    0, 0, pixels.width, pixels.height
  );
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        const name = (fileName || "photo").replace(/\.[^.]+$/, "") + ".jpg";
        resolve(new File([blob], name, { type: "image/jpeg" }));
      },
      "image/jpeg",
      0.92
    );
  });
}

const ImageCropDialog = ({ open, file, cropShape = "round", aspect = 1, title = "Position your photo", onCancel, onCropped }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!file) { setImageSrc(null); return; }
    const url = URL.createObjectURL(file);
    setImageSrc(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onCropComplete = useCallback((_area, areaPixels) => setPixels(areaPixels), []);

  const handleConfirm = async () => {
    if (!imageSrc || !pixels) return;
    setSaving(true);
    try {
      const cropped = await getCroppedFile(imageSrc, pixels, file?.name);
      await onCropped(cropped);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onCancel?.(); }}>
      <DialogContent className="max-w-md p-0 overflow-hidden gap-0" data-testid="image-crop-dialog">
        <div className="px-5 pt-5 pb-3 bg-[#1A1A1A] text-white">
          <h3 className="font-serif text-xl">{title}</h3>
          <p className="text-xs text-white/70 mt-0.5">Drag to move, pinch or use the slider to zoom.</p>
        </div>

        <div className="relative w-full h-72 bg-[#2E2A26]">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              cropShape={cropShape}
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>

        <div className="px-5 py-4 bg-[#FBF7F1] space-y-4">
          <div className="flex items-center gap-3">
            <ZoomIn className="w-4 h-4 text-[#2E2A26]/50 flex-shrink-0" />
            <Slider
              value={[zoom]}
              min={1}
              max={3}
              step={0.01}
              onValueChange={(v) => setZoom(v[0])}
              data-testid="crop-zoom-slider"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={saving}
              className="rounded-full"
              data-testid="crop-cancel-btn"
            >
              <X className="w-4 h-4 mr-1.5" /> Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirm}
              disabled={saving || !pixels}
              className="rounded-full bg-[#C85A38] hover:bg-[#A84830] text-white"
              data-testid="crop-confirm-btn"
            >
              <Check className="w-4 h-4 mr-1.5" /> {saving ? "Saving..." : "Use photo"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropDialog;
